<template>
  <div v-if="isBannerEligible">
    <div :key="forceRerenderKey" ref="bannerContainer" data-test-id="loyal-list-banner" class="loyalList" v-html="banner"></div>

    <PopUP v-if="showPopUp" ref="popUpRef" :mediacontent="mediacontent" @close="handlePopUpClose" />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, onBeforeUnmount, ref, watch, nextTick } from 'vue';
import useLoyalList from '~/composables/useLoyalList';
import useExperiments from '~/stores/useExperimentsStore';
import createPopUpLinkHandler from '../utils/handlePopUpLink';

const {
  experimentFlags: { isSplitPerksEnabled, isSplitPerksV2Enabled, isSplitPerksV2EnabledSignedIn },
} = useExperiments();

const { banner, forceRerenderKey } = useLoyalList();

const PopUP = defineAsyncComponent(() => import('@nuxt-common/canvas-aem/app/components/popUp/PopUp.vue'));

const popUpRef = ref<InstanceType<typeof PopUP> | null>(null);
const mediacontent = ref({});
const bannerContainer = ref<HTMLElement | null>(null);
const attachedHandlers = new Map();

const showPopUp = ref(false);
const pendingToggleId = ref<string | null>(null);

const isBannerEligible = computed(() => !isSplitPerksEnabled && !isSplitPerksV2Enabled && !isSplitPerksV2EnabledSignedIn && banner.value);

const toggle = (id: string) => {
  showPopUp.value = true;
  pendingToggleId.value = id;
};

const handlePopUpClose = () => {
  showPopUp.value = false;
  mediacontent.value = {};
};

const removeListeners = () => {
  attachedHandlers.forEach((handler, item) => {
    if (item && item.parentNode) {
      item.removeEventListener('click', handler, true);
    }
  });
  attachedHandlers.clear();
};

const attachListeners = async () => {
  removeListeners();

  await nextTick();

  const link = bannerContainer.value?.querySelector('a[href*="popupOverlayId"], area[href*="popupOverlayId"]');

  if (link) {
    const handler = createPopUpLinkHandler(mediacontent, toggle);
    link.addEventListener('click', handler, true);
    attachedHandlers.set(link, handler);
  }
};

onMounted(async () => {
  if (isBannerEligible.value) {
    await attachListeners();
  }
});

watch(popUpRef, (newPopUpRefInstance) => {
  if (newPopUpRefInstance && pendingToggleId.value !== null) {
    if (typeof newPopUpRefInstance.toggleModal === 'function') {
      newPopUpRefInstance.toggleModal(pendingToggleId.value);
    }
    pendingToggleId.value = null;
  }
});

onBeforeUnmount(() => {
  removeListeners();
});
</script>

<style lang="scss">
@import '~settingSCSS';
.loyalList {
  .purple {
    color: $loyalty-primary-1-color;
  }
  .pink {
    color: $highlight-color;
  }
}
</style>
